import React, { useEffect, useRef, useCallback, memo } from 'react'
import { Modal } from 'react-components'
import {
    ImageOuterContainer,
    StickyHeaderContainer,
    StickyFooterContainer,
    ScrollableContainer,
    SectionTitle,
    FullScreenVideoContainer,
    fullScreenModalStyles,
} from './styles'
import type { FullScreenModalProps } from './types'
import VideoPlayer from '../../../common/video-player'
import { CategoryBrowseTab, Image } from './types'
import FullScreenImageItem from './full-screen-image-item'
import { getCategory } from '../../../../utils/category-utils'
import { usePropertyDetailsContext } from '../../../../hooks/property-details'
import { virtualTourVideoEventLabels } from '../../../../services/analytics-service/labels'
import CategoryTabBar from './category-tab-bar'
import { useGallerySections } from './hooks/use-gallery-sections'

const FullScreenModal: React.FC<FullScreenModalProps> = ({
    images,
    video,
    isFullScreenOpen,
    isOverlayOpen,
    setFullScreen,
    header,
    footer,
    setOverlay,
    setSelectedImage,
    currentIndex,
    onFullscreenImageClick,
    onFullScreenOpen,
    shouldAutoplayVideo = false,
    enableCategorizedGallery = false,
    availableTabs = [],
    activeCategoryTab = CategoryBrowseTab.ALL,
    onCategoryTabChange,
    getOverlayIndex,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const { isComboListing } = usePropertyDetailsContext()

    const closeModal = useCallback(() => {
        setFullScreen(false)
    }, [setFullScreen])

    // Runs on open only -- adding currentIndex would re-scroll the grid on every slide change.
    useEffect(() => {
        if (isFullScreenOpen) {
            onFullScreenOpen?.()

            setTimeout(() => {
                if (scrollContainerRef.current) {
                    const targetElement = scrollContainerRef.current.querySelector(
                        `[data-image-id="full_screen_image_index_${currentIndex}"]`,
                    )

                    if (targetElement) {
                        targetElement.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center',
                        })
                    }
                }
            }, 0)
        }
    }, [isFullScreenOpen])

    useEffect(() => {
        scrollContainerRef.current?.scrollTo({ top: 0 })
    }, [activeCategoryTab])

    const handleItemClick = useCallback(
        (index: number) => {
            onFullscreenImageClick?.(index)
            setSelectedImage(index)
            setTimeout(() => setOverlay(true), 0)
        },
        [setSelectedImage, setOverlay, onFullscreenImageClick],
    )

    const { videoSectionTitle, imageSections } = useGallerySections({
        images,
        enableCategorizedGallery,
        availableTabs,
        activeCategoryTab,
    })

    const renderImageItem = useCallback(
        (image: Image) => {
            const overlayIndex = getOverlayIndex(image)
            return (
                <FullScreenImageItem
                    key={`image_full_screen_${overlayIndex}`}
                    image={image}
                    overlayIndex={overlayIndex}
                    onItemClick={handleItemClick}
                />
            )
        },
        [getOverlayIndex, handleItemClick],
    )

    return (
        <Modal isOpen={isFullScreenOpen} onRequestClose={closeModal} style={fullScreenModalStyles} ariaHideApp={false}>
            {!!header && (
                <StickyHeaderContainer enableCategorizedGallery={enableCategorizedGallery}>
                    {header({ onClose: closeModal, activeImageIndex: currentIndex })}
                </StickyHeaderContainer>
            )}
            {enableCategorizedGallery && !!onCategoryTabChange && (
                <CategoryTabBar tabs={availableTabs} activeTab={activeCategoryTab} onTabChange={onCategoryTabChange} />
            )}
            <ScrollableContainer ref={scrollContainerRef} enableCategorizedGallery={enableCategorizedGallery}>
                {!!video && (
                    <>
                        <SectionTitle>{videoSectionTitle}</SectionTitle>
                        <ImageOuterContainer>
                            <FullScreenVideoContainer data-image-id="full_screen_image_index_0">
                                <VideoPlayer
                                    src={video.src}
                                    isActive={!isOverlayOpen}
                                    autoplay={shouldAutoplayVideo}
                                    analyticsCategory={getCategory(isComboListing)}
                                    analyticsLabel={virtualTourVideoEventLabels.FullScreenGridModal}
                                />
                            </FullScreenVideoContainer>
                        </ImageOuterContainer>
                    </>
                )}
                {imageSections.map(
                    section =>
                        section.images.length > 0 && (
                            <React.Fragment key={section.key}>
                                <SectionTitle>{section.title}</SectionTitle>
                                <ImageOuterContainer>
                                    {section.images.map(image => renderImageItem(image))}
                                </ImageOuterContainer>
                            </React.Fragment>
                        ),
                )}
            </ScrollableContainer>
            {!!footer && (
                <StickyFooterContainer>
                    {footer({ onClose: closeModal, activeImageIndex: currentIndex })}
                </StickyFooterContainer>
            )}
        </Modal>
    )
}

export default memo(FullScreenModal)
